import axios, { AxiosError, AxiosRequestConfig } from 'axios'

/* eslint-disable @typescript-eslint/no-explicit-any */

import { ApiError, StandardResponse } from './types'
import * as tokenStorage from './tokenStorage'

const baseURL = process.env.NEXT_PUBLIC_API_BASE_URL

const AUTH_ENDPOINTS_EXEMPT_FROM_REFRESH = [
  '/auth/login',
  '/auth/verify-login-otp',
  '/auth/verify-login-totp',
  '/auth/recovery-login',
  '/auth/refresh-token',
  '/auth/student/register',
  '/auth/student/verify-otp',
]

let unauthorizedHandler: (() => void) | null = null

export const setUnauthorizedHandler = (handler: () => void): void => {
  unauthorizedHandler = handler
}

interface RetryableRequestConfig extends AxiosRequestConfig {
  _retry?: boolean
}

export const client = axios.create({ baseURL })

client.interceptors.request.use((config) => {
  const accessToken = tokenStorage.getAccessToken()
  if (accessToken) {
    config.headers = config.headers ?? {}
    config.headers.Authorization = `Bearer ${accessToken}`
  }
  return config
})

const toApiError = (error: unknown): ApiError => {
  if (axios.isAxiosError(error)) {
    // no response at all (offline, server down): say so in words, not axios's "Network Error"
    if (!error.response && error.code !== 'ERR_CANCELED') return new ApiError("We couldn't reach Purpose Connect. Check your connection and try again.", 0)
    const envelope = error.response?.data as StandardResponse<unknown> | undefined
    return new ApiError(envelope?.message ?? error.message, envelope?.status ?? error.response?.status ?? 0, envelope?.errors ?? null)
  }
  if (error instanceof ApiError) return error
  return new ApiError(error instanceof Error ? error.message : 'Unknown error', 0)
}

let refreshPromise: Promise<string> | null = null

const refreshAccessToken = async (): Promise<string> => {
  const refreshToken = tokenStorage.getRefreshToken()
  if (!refreshToken) throw new ApiError('No refresh token available', 401)

  const response = await axios.post<StandardResponse<{ accessToken: string }>>(`${baseURL}/auth/refresh-token`, { refreshToken })
  const { accessToken } = response.data.data
  tokenStorage.setAccessToken(accessToken)
  return accessToken
}

client.interceptors.response.use(
  (response) => {
    const envelope = response.data as StandardResponse<unknown>
    if (envelope && typeof envelope === 'object' && 'success' in envelope && envelope.success === false) {
      throw new ApiError(envelope.message, envelope.status, envelope.errors)
    }
    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryableRequestConfig | undefined
    const requestUrl = originalRequest?.url ?? ''
    const isExemptFromRefresh = AUTH_ENDPOINTS_EXEMPT_FROM_REFRESH.some((endpoint) => requestUrl.includes(endpoint))

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry && !isExemptFromRefresh) {
      originalRequest._retry = true

      try {
        refreshPromise ??= refreshAccessToken().finally(() => {
          refreshPromise = null
        })
        const newAccessToken = await refreshPromise
        originalRequest.headers = originalRequest.headers ?? {}
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
        return client(originalRequest)
      } catch (refreshError) {
        tokenStorage.clearTokens()
        unauthorizedHandler?.()
        return Promise.reject(toApiError(refreshError))
      }
    }

    return Promise.reject(toApiError(error))
  },
)

export interface PaginatedResult<T> {
  data: T
  metadata: StandardResponse<T>['metadata']
}

const unwrap = <T>(promise: Promise<{ data: StandardResponse<T> }>): Promise<T> => promise.then((response) => response.data.data)

const unwrapPaginated = <T>(promise: Promise<{ data: StandardResponse<T> }>): Promise<PaginatedResult<T>> =>
  promise.then((response) => ({ data: response.data.data, metadata: response.data.metadata }))

export const apiGet = <T>(url: string, config?: AxiosRequestConfig): Promise<T> => unwrap(client.get(url, config))

export const apiGetPaginated = <T>(url: string, config?: AxiosRequestConfig): Promise<PaginatedResult<T>> => unwrapPaginated(client.get(url, config))

export const apiPost = <T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> => unwrap(client.post(url, body, config))

export const apiPatch = <T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> => unwrap(client.patch(url, body, config))

export const apiDelete = <T>(url: string, config?: AxiosRequestConfig): Promise<T> => unwrap(client.delete(url, config))
